import WhatsAppIcon from "@/components/WhatsAppIcon";
import { usePageData } from "@/lib/pageData";
import { SITE_URL } from "@/lib/business";
import { ServiceGuide } from "@/components/ServiceGuide";
import { ServiceArea } from "@/components/ServiceArea";
import { serviceAreasSchema } from "@/lib/serviceAreas";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ClipboardList, MessageSquare, FileCheck, CheckCircle2 } from "lucide-react";
import { api, waLink } from "@/lib/api";
import { useSettings } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { EnquiryForm } from "@/components/EnquiryForm";

const PROCESS = [
  [ClipboardList, "Share your requirement", "Tell us what you need through the form or WhatsApp."],
  [MessageSquare, "Consultation", "We discuss your case and explain the exact steps and charges."],
  [FileCheck, "Information & filing", "You share the required information; we prepare and coordinate the application."],
  [CheckCircle2, "Follow-through", "We track progress and keep you updated until completion."],
];

export default function ServicePage() {
  const { slug } = useParams();
  const settings = useSettings();
  const initial = usePageData();
  const [storedService, setService] = useState(initial?.service || null);
  const service = storedService?.slug === slug ? storedService : initial?.service || null;
  const [missingSlug, setMissingSlug] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    let active = true;
    setService(initial?.service || null);
    setMissingSlug(null);
    setOpenFaq(0);
    api.get('/services/' + slug).then((r) => { if (active) setService(r.data); }).catch((error) => {
      if (active && (error?.response?.status === 404 || !initial?.service)) setMissingSlug(slug);
    });
    return () => { active = false; };
  }, [slug, initial]);

  if (missingSlug === slug) {
    return (
      <div className="container-x py-24 text-center" data-testid="service-not-found">
        <SEO title="Service Not Found | Mynavodayans" description="This service is unavailable or does not exist." noindex />
        <h1 className="font-heading text-2xl font-bold text-[#0a192f]">Service not found or currently unavailable</h1>
        <p className="mt-3 text-slate-600">Browse the full directory or send us an enquiry — we may still be able to help.</p>
        <Link to="/services" className="btn-navy mt-6">Browse all services</Link>
      </div>
    );
  }
  if (!service) return <><SEO title="Service Details | Mynavodayans" description="Loading service details." noindex /><p className="container-x py-24 text-center text-slate-500" data-testid="service-loading">Loading…</p></>;

  const alternateNames = (service.listed_as || []).filter((name) => name.toLowerCase() !== service.name.toLowerCase());
  const waMsg = `Hello Mynavodayans, I would like to enquire about ${service.name}.`;
  const isDev = service.category_slug === "website-app-development";
  const isProfessional = service.service_type === "professional";
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Service",
      "@id": SITE_URL + `/services/${slug}#service`,
      name: service.name, description: service.short_description,
      alternateName: service.listed_as || [],
      provider: { "@id": SITE_URL + "/#organization", "@type": "Organization", name: "Mynavodayans Online Services Private Limited", url: SITE_URL + "/", telephone: "+91 9424706212" },
      url: SITE_URL + `/services/${slug}`,
      serviceType: service.category_name,
      mainEntityOfPage: { "@id": SITE_URL + `/services/${slug}#webpage` },
      areaServed: serviceAreasSchema(),
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Services", item: SITE_URL + "/services" },
        { "@type": "ListItem", position: 3, name: service.category_name, item: `${SITE_URL}/services/category/${service.category_slug}` },
        { "@type": "ListItem", position: 4, name: service.name, item: SITE_URL + `/services/${slug}` },
      ],
    },
  ];

  return (
    <div data-testid="service-page">
      <SEO title={service.seo_title || `${service.name} | Mynavodayans`}
           description={service.seo_description || service.short_description}
           canonicalPath={`/services/${slug}`} jsonLd={jsonLd} />

      <section className="bg-[#0a192f] py-14 text-white">
        <div className="container-x">
          <nav aria-label="Breadcrumb" className="text-xs text-white/60" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-[#f5c542]">Home</Link> <span aria-hidden="true">/</span>{" "}
            <Link to="/services" className="hover:text-[#f5c542]">Services</Link> <span aria-hidden="true">/</span>{" "}
            <Link to={`/services/category/${service.category_slug}`} className="hover:text-[#f5c542]">{service.category_name}</Link> <span aria-hidden="true">/</span>{" "}
            <span className="font-semibold text-[#f5c542]">{service.name}</span>
          </nav>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl" data-testid="service-title">{service.name}</h1>
          <p className="mt-4 max-w-2xl text-white/75 md:text-lg" data-testid="service-description">{service.short_description}</p>
          {alternateNames.length > 0 && (
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300" data-testid="service-alternate-names">
              Also listed as: {alternateNames.join(" · ")}
            </p>
          )}
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={`/contact?service=${service.slug}`} className="btn-gold" data-testid="service-quote-btn">Request a Quote <ArrowRight size={16} /></Link>
            <a href={waLink(settings.whatsapp_number, waMsg)} target="_blank" rel="noopener noreferrer" className="btn-outline-light" data-testid="service-whatsapp-btn">
              <WhatsAppIcon size={16} /> WhatsApp about {service.name.split("(")[0].trim()}
            </a>
          </div>
        </div>
      </section>

      <section className="container-x grid gap-12 py-16 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <h2 className="font-heading text-2xl font-extrabold text-[#0a192f]">Scope of Assistance</h2>
          <ul className="mt-5 space-y-3 text-slate-700" data-testid="service-scope">
            {(isDev ? [
              `Requirement discussion and scope finalisation for your ${service.name.toLowerCase()} project.`,
              "Clear quotation covering design, development and delivery milestones.",
              "Regular progress updates and review cycles until handover.",
            ] : isProfessional ? (service.scope || [service.short_description]) : [
              `Understanding your requirement for ${service.name} and confirming applicability to your business.`,
              "Guidance on the information and documents you will need to arrange.",
              "Preparation and coordination of the application or registration with the relevant portal or authority.",
              "Status follow-up and clear communication until the process concludes.",
            ]).map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed">
                <CheckCircle2 className="mt-0.5 shrink-0 text-[#d4a017]" size={18} /> {item}
              </li>
            ))}
          </ul>
          {!isDev && !isProfessional && (
            <p className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900" data-testid="service-disclaimer">
              Mynavodayans is a private service provider. We do not issue licences or approvals — these are granted solely by the relevant government authority. We do not guarantee approval outcomes.
            </p>
          )}

          <ServiceGuide guide={service.guide} />
          <ServiceArea service={service} />
          {!service.guide && (<>
<h2 className="mt-12 font-heading text-2xl font-extrabold text-[#0a192f]">Enquiry & Consultation Process</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="service-process">
            {PROCESS.map(([Icon, title, text], i) => (
              <div key={title} className="rounded-xl border border-slate-200 bg-white p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0a192f] text-[#f5c542]"><Icon size={18} /></span>
                <h3 className="mt-3 font-heading text-sm font-bold text-[#0a192f]">{i + 1}. {title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>


          </>)}
          <h2 className="mt-12 font-heading text-2xl font-extrabold text-[#0a192f]">FAQs</h2>
          <div className="mt-5 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white" data-testid="service-faqs">
            {(service.faqs || []).map((f, i) => (
              <div key={i}>
                <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-bold text-[#0a192f]"
                        onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i} aria-controls={"service-faq-answer-" + i} data-testid={`service-faq-toggle-${i}`}>
                  {f.q}
                  <span className={`text-[#d4a017] transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</span>
                </button>
                <p id={"service-faq-answer-" + i} hidden={openFaq !== i} className="px-5 pb-4 text-sm leading-relaxed text-slate-600" data-testid={`service-faq-answer-${i}`}>{f.a}</p>
              </div>
            ))}
          </div>

          {service.related?.length > 0 && (
            <>
              <h2 className="mt-12 font-heading text-2xl font-extrabold text-[#0a192f]">Related Services</h2>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="related-services">
                {service.related.map((r) => (
                  <Link key={r.slug} to={`/services/${r.slug}`}
                        className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:border-[#d4a017] hover:bg-amber-50"
                        data-testid={`related-${r.slug}`}>{r.name}</Link>
                ))}
              </div>
            </>
          )}
        </div>

        <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-6 shadow-lg lg:sticky lg:top-24" data-testid="service-enquiry-panel">
          <EnquiryForm prefillService={service.slug} sourcePage={`/services/${slug}`} heading={`Enquire: ${service.name.split("(")[0].trim()}`} />
        </aside>
      </section>
    </div>
  );
}
